import { canonicalGroupForToken } from './canonical-aliases';
import { normalizeUnitToken } from './normalize-unit-token';

/** The minimum a catalog row needs to be matched against: its canonical code
 *  plus the spellings owner data actually prints. */
export interface UnitCatalogEntry {
  code: string;
  variants?: string[] | null;
}

/**
 * THE unit-resolution path: a raw printed unit → the catalog row it means, in
 * three tiers.
 *
 *   1. Exact normalized match on the code or any variant — punctuation and case
 *      insensitive ("Ea." → "ea" matches code "EA", "S.Y." matches "SY").
 *   2. Canonical alias table (the closed set of US construction units) — pivot
 *      the token to its concept group ("TONS" → `ton`, "LIN FT" → `lf`), then
 *      take the catalog row whose code/variant lives in that group.
 *   3. null — genuinely novel (a typo, a vendor-specific oddity). The caller
 *      KEEPS the printed unit and offers it as an unknown/custom unit; a miss
 *      here is never a reason to blank the unit.
 *
 * Ties break on the caller's array order, so a catalog listed by code resolves
 * identically on every run. Safe to import from server OR client code.
 */
export function resolveUnitAgainstCatalog<T extends UnitCatalogEntry>(
  raw: string | null | undefined,
  units: readonly T[],
): T | null {
  const key = normalizeUnitToken(raw);
  if (!key) {
    return null;
  }

  for (const unit of units) {
    if (normalizeUnitToken(unit.code) === key) {
      return unit;
    }
    if (unit.variants?.some((v) => normalizeUnitToken(v) === key)) {
      return unit;
    }
  }

  const group = canonicalGroupForToken(key);
  if (!group) {
    return null;
  }
  for (const unit of units) {
    if (canonicalGroupForToken(normalizeUnitToken(unit.code)) === group) {
      return unit;
    }
    if (
      unit.variants?.some(
        (v) => canonicalGroupForToken(normalizeUnitToken(v)) === group,
      )
    ) {
      return unit;
    }
  }
  return null;
}
